import React, { FunctionComponent } from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import moment from 'moment';

import { Translate } from 'react-localize-redux';

import styled, { css } from 'styled-components';

// Types
import SimpleArtist from '@common/types/artist/simple-artist';
import CatalogRelease from '@common/types/catalog/catalog-release';
import CatalogFilter from '@common/types/catalog/catalog-filter-type';

// Store
import AppState from '@webapp/store/state';
import { showMainLoader } from '@webapp/store/ui/selectors';
import { CleanRelease } from '@webapp/store/release/action-creators';

// Components
import EmptySectionBox from '../empty-wrapper';

import Grid from '@components/basics/grid';
import CatalogRow from '@components/catalog-row';
import Typography from '@components/basics/typography';
import NoResults from '../no-results';

interface CatalogTabProps {
  list: CatalogRelease[];
  activeArtist: SimpleArtist;
  allReleases: CatalogRelease[];
  isMobile: boolean;
  isReleases: boolean;
  loading: boolean;
  isPlanFeature: boolean;
  setReleasesListLength?: React.Dispatch<React.SetStateAction<number>>;
  cleanRelease: () => CleanRelease;
  push: (path: string) => CallHistoryMethodAction;
  filterOption: CatalogFilter;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
  loading: showMainLoader(state),
  activeArtist: state.artist.active as SimpleArtist,
  isPlanFeature:
    state.subscription.current?.plan.toLocaleLowerCase() === 'beginner',
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  cleanRelease: () => dispatch(new CleanRelease()),
  push: (path: string) => dispatch(push(path)),
});

const HEADER_TITLES = [
  { id: 'release', label: <Translate id="CATALOG.HEADER.RELEASE" /> },
  { id: 'date-added', label: <Translate id="CATALOG.HEADER.DATE_ADDED" /> },
  { id: 'release-date', label: <Translate id="CATALOG.HEADER.RELEASE_DATE" /> },
  { id: 'status', label: <Translate id="CATALOG.HEADER.STATUS" /> },
];

const filterRelease = (
  i: CatalogRelease,
  filterOption: CatalogFilter | undefined
) => {
  let filter = true;
  if (filterOption) {
    if (filterOption.searchText)
      filter =
        filter &&
        i.title
          .toLocaleLowerCase()
          .includes(filterOption.searchText.toLocaleLowerCase());
    if (filterOption.dateAdded)
      filter =
        filter &&
        moment(new Date(i.created_at)).isBetween(
          new Date(filterOption.dateAdded.startDate),
          new Date(filterOption.dateAdded.endDate)
        );
    if (filterOption.releaseDate)
      filter =
        filter &&
        moment(new Date(i.release_date || new Date())).isBetween(
          new Date(filterOption.releaseDate.startDate),
          new Date(filterOption.releaseDate.endDate)
        );
    if (filterOption.status) {
      if (filterOption.status.length === 0) return filter;
      const isPublished = moment().isSameOrAfter(moment(i.release_date));

      filter =
        filter &&
        (filterOption.status.includes(
          i.status === 'published' &&
            isPublished &&
            (i.approved || i.approved === undefined)
            ? 'released'
            : i.status === 'pending-processing'
            ? 'pending'
            : i.status
        ) ||
          filterOption.status.includes(
            i.status === 'published' &&
              !isPublished &&
              (i.approved || i.approved === undefined)
              ? 'approved'
              : i.status === 'pending-processing'
              ? 'pending'
              : i.status
          ));
    }
  }

  return filter;
};

const sortTakedownPending = (a: CatalogRelease, b: CatalogRelease) => {
  if (a.status === 'takedown-pending' && b.status !== 'takedown-pending') {
    return -1;
  }
  if (a.status !== 'takedown-pending' && b.status === 'takedown-pending') {
    return 1;
  }
  return 0;
};

// TODO: DEVTEAM-603 - const isMultipleArtist = false; needs to be integrated

const CatalogTab: FunctionComponent<CatalogTabProps> = ({
  list,
  activeArtist,
  allReleases,
  isMobile,
  isReleases,
  cleanRelease,
  push,
  loading,
  isPlanFeature,
  setReleasesListLength,
  filterOption,
}) => {
  React.useEffect(() => {
    if (setReleasesListLength) {
      return setReleasesListLength(list.length);
    }
  }, [setReleasesListLength, list]);

  if ((list.length === 0 || !list) && !loading) {
    return (
      <EmptySectionBox
        header={<Translate id="CATALOG.MUSIC.EMPTY.TITLE" />}
        title={<Translate id="CATALOG.MUSIC.EMPTY.ADD.TITLE" />}
        text={<Translate id="CATALOG.MUSIC.EMPTY.ADD.TEXT" />}
        onClick={() => {
          if (!isPlanFeature) {
            cleanRelease();
            push('/release');
          } else {
            push('/release-subscription-plan');
          }
        }}
      />
    );
  }

  const getUpdatedReleases = () => {
    return list
      .filter((i) => filterRelease(i, filterOption))
      .sort(sortTakedownPending);
  };

  return (
    <div>
      {!isMobile && (
        <TableTitles>
          {HEADER_TITLES.map((item) => (
            <div className={item.id} key={item.id}>
              <Typography weight="700">{item.label}</Typography>
            </div>
          ))}
        </TableTitles>
      )}
      <CatalogTabWrapper isMobile={isMobile}>
        {!!getUpdatedReleases().length ? (
          getUpdatedReleases().map((item, i) => (
            <CatalogRow
              item={item}
              artist={activeArtist}
              release={allReleases.find((r) => r.id === item.id)}
              isReleases={isReleases}
              key={`${item.id}#${i}`}
              isFilteredByAddedDate={!!filterOption?.dateAdded}
            />
          ))
        ) : (
          <NoResults />
        )}
      </CatalogTabWrapper>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CatalogTab);

const CatalogTabWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-gap: 10px;

  ${(props) =>
    !props.isMobile
      ? css`
          position: relative;
        `
      : ''}
`;

const TableTitles = styled(Grid)`
  grid-template-columns: repeat(18, minmax(0, 1fr));
  grid-gap: 30px;
  align-items: center;
  padding: 0 30px 30px;

  .release {
    grid-column: 1 / 7;
  }
  .date-added {
    grid-column: 7 / 10;
  }
  .release-date {
    grid-column: 10 / 13;
  }
  .status {
    grid-column: 13 / 17;
  }
  .actions {
    grid-column: 17 / 19;
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    .release {
      grid-column: 1 / 9;
    }
    .date-added {
      grid-column: 9 / 12;
    }
    .release-date {
      grid-column: 12 / 15;
    }
    .status {
      grid-column: 15 / 17;
    }
    .actions {
      grid-column: 17 / 19;
    }
  }
`;
